import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import { DeletionFields, useDeletionFields } from '../components/DeleteWithReason';
import { useSession } from '../lib/session';
import type { CompanyRole, CompanySummary, PersonRow } from '../lib/types';
import { ROLE_HINTS, ROLE_LABELS } from '../lib/types';
import { Modal } from '../components/Modal';
import { formatDate } from '../lib/dates';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { outcomeOf, type Handover } from '../lib/handover';

export function PeoplePage() {
  /* A page that IS a table: it gets the window, not the reading column. */
  const { session } = useSession();
  const [people, setPeople] = useState<PersonRow[] | null>(null);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [granting, setGranting] = useState<PersonRow | null>(null);
  const [deleting, setDeleting] = useState<PersonRow | null>(null);
  /**
   * The id, never the row.
   *
   * Holding the row meant the panel kept whatever was true when it opened: making somebody an
   * administrator refreshed the list underneath, and the panel went on offering "Make admin"
   * until it was closed and opened again. Looking the person up by id each render means an
   * action taken inside the panel shows in the panel.
   */
  const [managingId, setManagingId] = useState<string | null>(null);
  const managing = managingId ? ((people ?? []).find((p) => p.id === managingId) ?? null) : null;
  const [handover, setHandover] = useState<Handover | null>(null);

  const isAdmin = !!session?.person.isAdmin;

  const load = useCallback(async () => {
    try {
      const [peopleRows, companyRows] = await Promise.all([
        api.get<PersonRow[]>('/people'),
        api.get<CompanySummary[]>('/companies'),
      ]);
      setPeople(peopleRows);
      setCompanies(companyRows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load people');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The columns, and what each one MEANS as data.
   *
   * The company cell draws badges; `value` behind it is the codes and role names as text, so
   * searching "PORT" or "client" finds the right people and the export is readable. The
   * account cell is the same idea — the words behind the badges are what somebody filters on
   * ("admin", "2-step: app"), not the badges themselves.
   */
  const columns: Column<PersonRow>[] = useMemo(
    () => [
      {
        key: 'person',
        header: 'Person',
        width: '26%',
        value: (person) => person.name,
        filter: 'text',
        render: (person) => (
          <>
            <div style={{ fontWeight: 600 }}>{person.name}</div>
            <div className="dim" style={{ fontSize: 12 }}>
              {person.email}
              {person.jobTitle ? ` · ${person.jobTitle}` : ''}
            </div>
          </>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        hiddenByDefault: true,
        value: (person) => person.email,
        filter: 'text',
        render: (person) => <span className="muted">{person.email}</span>,
      },
      {
        key: 'companies',
        header: 'Companies & roles',
        value: (person) =>
          person.isAdmin
            ? `Administrator ${person.companies.map((company) => company.code).join(' ')}`
            : person.companies.map((company) => `${company.code} ${ROLE_LABELS[company.role]}`).join(', '),
        filter: 'text',
        render: (person) =>
          person.isAdmin ? (
            <span className="dim" style={{ fontSize: 12 }}>
              Every company, as an administrator
              {person.companies.length > 0 && ` · also ${person.companies.map((c) => c.code).join(', ')}`}
            </span>
          ) : person.companies.length === 0 ? (
            <span className="dim">No company access</span>
          ) : (
            <div className="row wrap" style={{ gap: 5 }}>
              {person.companies.map((company) => (
                <span key={company.id} className="badge" title={ROLE_LABELS[company.role]}>
                  {company.code} · {ROLE_LABELS[company.role]}
                </span>
              ))}
            </div>
          ),
      },
      {
        key: 'account',
        header: 'Account',
        value: (person) =>
          [
            person.isAdmin ? 'admin' : '',
            person.isTester ? 'tester' : '',
            person.isFinance ? 'billing' : '',
            person.isActive ? 'active' : 'deactivated',
            person.twoFactorMode === 'app' ? '2-step: app' : person.twoFactorMode === 'email' ? '2-step: email' : '',
          ]
            .filter(Boolean)
            .join(', '),
        filter: 'text',
        render: (person) => (
          <div className="row wrap" style={{ gap: 5 }}>
            {person.isAdmin && <span className="badge badge-accent">admin</span>}
            {person.isTester && <span className="badge badge-info">tester</span>}
            {person.isFinance && <span className="badge badge-info">billing</span>}
            {!person.isActive && <span className="badge">deactivated</span>}
            {/*
              Shown because it is the thing somebody rings up about: "I have a new phone and
              cannot get in." Knowing which method is on decides what to tell them before
              anybody clicks anything.
            */}
            {person.twoFactorMode !== 'off' && (
              <span
                className="badge badge-ok"
                title={
                  person.twoFactorMode === 'app'
                    ? 'Signs in with an authenticator app'
                    : 'Signs in with a code emailed to them'
                }
              >
                {person.twoFactorMode === 'app' ? '2-step: app' : '2-step: email'}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'lastLogin',
        header: 'Last signed in',
        /* The stored timestamp sorts; "never" is empty, so it sinks either way. */
        value: (person) => person.lastLoginAt ?? '',
        filter: 'none',
        render: (person) => (
          <span className="muted">{person.lastLoginAt ? formatDate(person.lastLoginAt) : 'never'}</span>
        ),
      },
      ...(isAdmin
        ? ([
            {
              key: 'actions',
              header: '',
              align: 'right',
              stickyRight: true,
              /* No `value`: an actions column is not data and takes no part in sort or export. */
              /*
                One button, not six. Every account action used to sit in this cell, which made
                the widest column on the page the one carrying the least information and left
                no room for the columns that matter. They are all still here, a click further
                in, where each one can be labelled and say what it does.
              */
              render: (person) => (
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-manage"
                    onClick={() => setManagingId(person.id)}
                  >
                    Manage
                    <span className="chevron" aria-hidden="true">▾</span>
                  </button>
                </div>
              ),
            },
          ] as Column<PersonRow>[])
        : []),
    ],
    [isAdmin, load],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Roles &amp; access</h1>
          <p className="subtitle">
            Accounts, and the company each person has a role on. Membership of a company is the
            outer gate — without it, a person sees nothing of that company at all.
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            Add person
          </button>
        )}
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      {handover && (
        <div className="callout callout-ok mb-2">
          {handover.kind === 'password' ? (
            <>
              <strong>Temporary password for {handover.name}</strong>
              <div className="mono" style={{ fontSize: 15, margin: '6px 0' }}>
                {handover.password}
              </div>
              Hand this over directly. It is shown once and cannot be retrieved again.
            </>
          ) : (
            <>
              <strong>Invitation sent to {handover.name}</strong>
              <div className="mono" style={{ fontSize: 14, margin: '6px 0' }}>{handover.email}</div>
              They set their own password from the link, so no password was generated and there
              is nothing for you to pass on. Send it again with <strong>Reset</strong> if it
              does not arrive.
            </>
          )}
          <div>
            <button type="button" className="btn btn-sm mt-2" onClick={() => setHandover(null)}>
              Done
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {people === null ? (
          <div className="empty">Loading…</div>
        ) : people.length === 0 ? (
          <div className="empty">
            <h3>No people yet</h3>
            <p>Add the accounts that will use the Hub.</p>
          </div>
        ) : (
          <div className="card-pad">
            {/*
              The shared list surface. A team of forty is a list somebody searches — "who has
              2-step on", "who has not signed in since June" — and before this it was a wall
              of rows in whatever order the API returned them.
            */}
            <DataTable
              tableKey="people"
              columns={columns}
              rows={people}
              searchPlaceholder="Find a person — name, email, company"
              defaultSort={{ key: 'person', direction: 'asc' }}
              rowClassName={(person) => (person.isActive ? undefined : 'row-muted')}
            />
          </div>
        )}
      </div>

      {creating && (
        <CreatePersonModal
          onClose={() => setCreating(false)}
          onCreated={(outcome) => {
            setCreating(false);
            // Always something. `if (password)` used to be the whole handling, so an invited
            // account closed the dialog silently and nothing on screen said it had been sent.
            setHandover(outcome);
            void load();
          }}
        />
      )}

      {managing && (
        <PersonActionsModal
          person={managing}
          onClose={() => setManagingId(null)}
          onChanged={load}
          onError={setError}
          onHandover={(outcome) => {
            setManagingId(null);
            setHandover(outcome);
          }}
          onRoles={() => {
            setGranting(managing);
            setManagingId(null);
          }}
          onDelete={() => {
            setDeleting(managing);
            setManagingId(null);
          }}
        />
      )}

      {deleting && (
        <DeleteAccountModal
          person={deleting}
          onClose={() => setDeleting(null)}
          onBack={() => {
            setManagingId(deleting.id);
            setDeleting(null);
          }}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}

      {granting && (
        <RolesModal
          person={granting}
          companies={companies}
          onClose={() => setGranting(null)}
          onBack={() => {
            setManagingId(granting.id);
            setGranting(null);
          }}
          onChanged={() => {
            void load();
          }}
        />
      )}
    </>
  );
}

/**
 * The administrator role, granted and removed from the list. Separate from company roles
 * because it is not one: an administrator reaches every company regardless, so this is the
 * only control that changes what they can see.
 */
function TesterToggle({
  person,
  onDone,
  onError,
}: {
  person: PersonRow;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  async function toggle() {
    const making = !person.isTester;
    const question = making
      ? `Make ${person.name} a tester? They will be able to move work through and send it back, on top of the role they already hold.`
      : `Remove ${person.name} as a tester? They keep their company role and lose only the testing.`;

    if (
      !(await ask.confirm({
        title: question,
        confirmLabel: making ? 'Make them a tester' : 'Take it off',
        danger: !making,
      }))
    ) {
      return;
    }

    setBusy(true);
    onError(null);
    try {
      if (making) await api.post(`/people/${person.id}/tester`);
      else await api.del(`/people/${person.id}/tester`);
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={person.isTester ? 'btn btn-danger btn-sm' : 'btn btn-quiet btn-sm'}
      disabled={busy}
      onClick={toggle}
      title={person.isTester ? 'Take the testing off' : 'Make this person a tester'}
    >
      {person.isTester ? 'Drop tester' : 'Make tester'}
    </button>
  );
}

/**
 * The billing hat. Granted exactly like the testing one, and for the same reason it is a hat:
 * whoever raises the invoices is already somebody here.
 */
function FinanceToggle({
  person,
  onDone,
  onError,
}: {
  person: PersonRow;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  async function toggle() {
    const making = !person.isFinance;
    const question = making
      ? `Put ${person.name} on the billing? They will see what each hour is worth and be able to mark time as invoiced, on top of the role they already hold.`
      : `Take ${person.name} off the billing? They keep their company role and lose only the invoicing.`;

    if (
      !(await ask.confirm({
        title: question,
        body: making
          ? 'They will not be able to approve time — approving says the hours are real, billing says they have been charged for, and the same person should not do both.'
          : undefined,
        confirmLabel: making ? 'Put them on billing' : 'Take it off',
        danger: !making,
      }))
    ) {
      return;
    }

    setBusy(true);
    onError(null);
    try {
      if (making) await api.post(`/people/${person.id}/finance`);
      else await api.del(`/people/${person.id}/finance`);
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={person.isFinance ? 'btn btn-danger btn-sm' : 'btn btn-quiet btn-sm'}
      disabled={busy}
      onClick={toggle}
      title={person.isFinance ? 'Take the billing off' : 'Put this person on the billing'}
    >
      {person.isFinance ? 'Drop billing' : 'Put on billing'}
    </button>
  );
}

function AdminToggle({
  person,
  onDone,
  onError,
}: {
  person: PersonRow;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  async function toggle() {
    const making = !person.isAdmin;
    const question = making
      ? `Make ${person.name} an administrator? They will be able to reach every company, create projects and manage accounts.`
      : `Remove ${person.name} as an administrator? They will be left with only their company roles.`;
    if (!(await ask.confirm({ title: question, confirmLabel: making ? 'Make them an administrator' : 'Drop the role', danger: !making }))) {
      return;
    }

    setBusy(true);
    onError(null);
    try {
      const result = making
        ? await api.post<{ companies: string[] }>(`/people/${person.id}/admin`)
        : await api.del<{ companies: string[] }>(`/people/${person.id}/admin`);
      onDone();
      if (!making) {
        onError(
          result.companies.length > 0
            ? `${person.name} is no longer an administrator, and now has access only to ${result.companies.join(', ')}.`
            : `${person.name} is no longer an administrator and has no company access at all. Grant them a role if they still need in.`,
        );
      }
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={person.isAdmin ? 'btn btn-danger btn-sm' : 'btn btn-quiet btn-sm'}
      disabled={busy}
      onClick={toggle}
      title={person.isAdmin ? 'Remove the administrator role' : 'Make this person an administrator'}
    >
      {person.isAdmin ? 'Drop admin' : 'Make admin'}
    </button>
  );
}

/**
 * Reset somebody's password.
 *
 * Two outcomes, again: where mail works this sends them a link and sets no password at all,
 * so that nobody but they ever knows it. This was typed as always returning a password, and
 * an emailed reset therefore showed the administrator a panel headed "Temporary password"
 * with nothing under it.
 */
function ResetPasswordButton({
  person,
  onDone,
}: {
  person: PersonRow;
  onDone: (outcome: Handover) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-quiet btn-sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const result = await api.post<{
            temporaryPassword: string | null;
            emailed: boolean;
            email: string;
          }>(`/people/${person.id}/password-reset`);
          onDone(outcomeOf(person.name, result.temporaryPassword, result.email));
        } finally {
          setBusy(false);
        }
      }}
    >
      Reset
    </button>
  );
}

function ToggleActiveButton({ person, onDone }: { person: PersonRow; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <button
        type="button"
        className={person.isActive ? 'btn btn-danger btn-sm' : 'btn btn-sm'}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await api.patch(`/people/${person.id}`, { isActive: !person.isActive });
            onDone();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not change the account');
          } finally {
            setBusy(false);
          }
        }}
      >
        {person.isActive ? 'Deactivate' : 'Reactivate'}
      </button>
      {error && <div className="field-error">{error}</div>}
    </>
  );
}

/**
 * True either way, which is why it says both.
 *
 * It used to promise "a temporary password and shows it to you once", and that is false
 * wherever SMTP is configured — the person is invited and no password is ever generated.
 * Nothing tells the browser which it will be, and an endpoint to say so is a lot of surface
 * for one sentence, so the sentence covers both outcomes instead.
 */
const SUBTITLE =
  'They are emailed an invitation to set their own password. Where mail is not configured, the Hub shows you a temporary password instead.';

function CreatePersonModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (outcome: Handover) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      /**
       * `invitedTo` is the address it actually went to, which is not always the one typed in
       * — the API trims and lower-cases it. Falling back to the typed address keeps the
       * message truthful if a future response omits the field.
       */
      const result = await api.post<{
        person: PersonRow;
        temporaryPassword: string | null;
        invited: boolean;
        invitedTo: string | null;
      }>('/people', {
        name,
        email,
        jobTitle: jobTitle || undefined,
        isAdmin,
      });
      onCreated(
        outcomeOf(result.person.name, result.temporaryPassword, result.invitedTo ?? email.trim()),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add person"
      subtitle={SUBTITLE}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="create-person" className="btn btn-primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add person'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}
      <form id="create-person" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">Full name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>

        <label className="field">
          <span className="field-label">Work email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>

        <label className="field">
          <span className="field-label">Job title (optional)</span>
          <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
        </label>

        <div className="checkbox-row">
          <input id="admin" type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
          <label htmlFor="admin">
            <strong>Administrator</strong>
            <div className="dim" style={{ fontSize: 12 }}>
              Creates companies, projects and accounts, and can work anywhere. More than one
              person can hold this.
            </div>
          </label>
        </div>

        <p className="field-hint">
          Everyone else is added to a company afterwards, as a team member or a client contact.
        </p>
      </form>
    </Modal>
  );
}

function RolesModal({
  person,
  companies,
  onClose,
  onChanged,
  onBack,
}: {
  person: PersonRow;
  companies: CompanySummary[];
  onClose: () => void;
  onChanged: () => void;
  /** Returns to the account's other actions. Absent when this was not opened from them. */
  onBack?: () => void;
}) {
  const [rows, setRows] = useState(person.companies);
  const [companyId, setCompanyId] = useState('');
  const [role, setRole] = useState<CompanyRole>('user');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const available = companies.filter((c) => !rows.some((r) => r.id === c.id));

  async function grant() {
    if (!companyId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/people/${person.id}/roles`, { companyId, role });
      const company = companies.find((c) => c.id === companyId)!;
      setRows([...rows, { id: company.id, name: company.name, code: company.code, role }]);
      setCompanyId('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not grant that role');
    } finally {
      setBusy(false);
    }
  }

  /** Moves an existing membership to another role. The API updates rather than duplicating. */
  async function changeRole(id: string, role: CompanyRole) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/people/${person.id}/roles`, { companyId: id, role });
      setRows(rows.map((r) => (r.id === id ? { ...r, role } : r)));
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that role');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/people/${person.id}/roles/${id}`);
      setRows(rows.filter((r) => r.id !== id));
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke that role');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Company access — ${person.name}`}
      subtitle="One role per company. A project can narrow it further."
      onClose={onClose}
      footer={
        <>
          {/*
            The way back to everything else you can do to this account. Without it this is a
            dead end: the only route here is through the actions panel, and closing to get back
            to it loses the row you were on.
          */}
          {onBack && (
            <button type="button" className="btn" onClick={onBack}>
              ← Back
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}

      {person.isAdmin && (
        <div className="callout">
          {person.name} is an administrator and can reach every company regardless of what is
          listed here. Use <strong>Drop admin</strong> in the list to change that.
        </div>
      )}

      {rows.length === 0 ? (
        <p className="dim">No company access yet.</p>
      ) : (
        <div className="stack-sm mb-2">
          {rows.map((row) => (
            <div key={row.id} className="row-between" style={{ padding: '7px 0', borderBottom: '1px solid var(--line)' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{row.name}</div>
                {/*
                  The role is changed here rather than revoked and re-granted. `grantRole`
                  updates a membership that already exists, so moving somebody from user to
                  client admin is one call and never leaves them with no access in between.
                */}
                <select
                  value={row.role}
                  disabled={busy || person.isAdmin}
                  style={{ fontSize: 12, marginTop: 3 }}
                  onChange={(e) => void changeRole(row.id, e.target.value as CompanyRole)}
                >
                  {(Object.keys(ROLE_LABELS) as CompanyRole[]).map((key) => (
                    <option key={key} value={key}>
                      {ROLE_LABELS[key]}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={busy || person.isAdmin}
                title={person.isAdmin ? 'An administrator reaches every company; drop admin instead' : undefined}
                onClick={() => revoke(row.id)}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      {available.length > 0 && (
        <div className="card card-pad" style={{ background: 'var(--surface-sunken)' }}>
          <div className="field-label">Grant access</div>
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">Company…</option>
              {available.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            <select value={role} onChange={(e) => setRole(e.target.value as CompanyRole)}>
              {(Object.keys(ROLE_LABELS) as CompanyRole[]).map((key) => (
                <option key={key} value={key}>
                  {ROLE_LABELS[key]}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" onClick={grant} disabled={busy || !companyId}>
              Grant
            </button>
          </div>
          <p className="field-hint">{ROLE_HINTS[role]}</p>
        </div>
      )}
    </Modal>
  );
}

/**
 * Clearing somebody else's second step — the "I have a new phone" button.
 *
 * Only offered when they actually have one on, so the row is not cluttered with an action
 * that would be refused. Confirmed first, because it lowers somebody's security and they will
 * not know until the next time they sign in.
 */
function ClearTwoFactorButton({
  person,
  onDone,
  onError,
}: {
  person: PersonRow;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const ask = useAsk();
  if (person.twoFactorMode === 'off') return null;

  const what = person.twoFactorMode === 'app' ? 'authenticator app' : 'emailed codes';

  return (
    <button
      type="button"
      className="btn btn-danger btn-sm"
      disabled={busy}
      title={`Turn off two-step sign-in for ${person.name}`}
      onClick={async () => {
        const sure = await ask.confirm({
          title: `Turn off two-step sign-in for ${person.name}?`,
          body:
            `They currently use ${what}. Their password alone will get them in until they set ` +
            'it up again, so only do this once you are sure it is really them asking.',
          confirmLabel: 'Turn it off',
          danger: true,
        });
        if (!sure) return;

        setBusy(true);
        try {
          await api.del(`/people/${person.id}/two-factor`);
          onDone();
        } catch (err) {
          onError(err instanceof ApiError ? err.message : 'Could not turn it off.');
        } finally {
          setBusy(false);
        }
      }}
    >
      Clear 2-step
    </button>
  );
}

/**
 * Deleting an account, with the reason the API requires.
 *
 * Confirmed by typing the EMAIL rather than the name: two people called Pierre Jonker is a
 * real possibility and two accounts on one address is not, so the address is the thing that
 * identifies the row being removed.
 *
 * The API refuses anyone still carrying work or booked time — deleting them would cascade
 * into their hours — and says so; that message is shown as written.
 */
function DeleteAccountModal({
  person,
  onClose,
  onDeleted,
  onBack,
}: {
  person: PersonRow;
  onClose: () => void;
  onDeleted: () => void;
  onBack?: () => void;
}) {
  const fields = useDeletionFields(person.email);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/people/${person.id}`, { reason: fields.reason.trim() });
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Delete ${person.name}?`}
      subtitle="For an account created by mistake. Someone who has left should be deactivated instead, so their work and hours keep their name."
      onClose={onClose}
      footer={
        <>
          {onBack && (
            <button type="button" className="btn" onClick={onBack}>
              ← Back
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" disabled={busy || !fields.ready} onClick={() => void submit()}>
            {busy ? 'Deleting…' : 'Delete account'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}
      <DeletionFields
        what="account"
        code={person.email}
        fields={fields}
        confirmHint="Their email address, so the right row is the one that goes."
      />
    </Modal>
  );
}

/**
 * Everything you can do to one account, in one place.
 *
 * These were six buttons crowded into a table cell, where the only thing distinguishing
 * "Clear 2FA" from "Deactivate" was the word on it — and two of them are irreversible. Given
 * room, each action can say what it does and what it will cost, which is the actual guard
 * against pressing the wrong one.
 *
 * The buttons themselves are unchanged and still own their own confirmation; this only gives
 * them somewhere to stand. Ordered by consequence: routine things first, the ones that end
 * access last.
 */
function PersonActionsModal({
  person,
  onClose,
  onChanged,
  onError,
  onHandover,
  onRoles,
  onDelete,
}: {
  person: PersonRow;
  onClose: () => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
  onHandover: (outcome: Handover) => void;
  onRoles: () => void;
  onDelete: () => void;
}) {
  return (
    <Modal
      title={person.name}
      subtitle={person.email}
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="stack-sm">
        <ActionRow
          label="Company access"
          detail={
            person.isAdmin
              ? 'An administrator reaches every company. Their memberships are listed here.'
              : 'Which companies they belong to, and the role they hold on each.'
          }
        >
          <button type="button" className="btn btn-sm" onClick={onRoles}>
            Roles…
          </button>
        </ActionRow>

        <ActionRow
          label="Administrator"
          detail={
            person.isAdmin
              ? 'Reaches every company, creates projects and manages accounts. Dropping it also takes back the memberships the role granted.'
              : 'Grants every company, and a membership of each one so they appear in its people list.'
          }
        >
          <AdminToggle person={person} onDone={onChanged} onError={onError} />
        </ActionRow>

        {/*
          Beside the administrator row and granted the same way, because it is the same kind of
          thing: something we add to an account, not a role somebody holds on a company.
          Their company role is untouched — which is what keeps a customer's own client admin
          able to go on managing them.
        */}
        <ActionRow
          label="Tester"
          detail={
            person.isTester
              ? 'Moves work through testing, sends it back with what failed, and says where it has got to. Their company role is unchanged.'
              : 'Adds testing to whatever they already are. They keep their company role — a client stays a client — and gain only the four things the job needs.'
          }
        >
          <TesterToggle person={person} onDone={onChanged} onError={onError} />
        </ActionRow>

        {/*
          NOT OFFERED FOR A CUSTOMER'S PERSON, which is where this parts company with the
          testing row above it. Testing is something we ask a customer to do; billing carries
          the rates and would scope to the one company they belong to, which is the customer
          marking their own hours invoiced. The API refuses it either way — this is so nobody
          is shown a button that only says no.
        */}
        {(person.isAdmin ||
          person.companies.length === 0 ||
          person.companies.some((company) => company.role !== 'client' && company.role !== 'client_admin')) && (
          <ActionRow
            label="Billing"
            detail={
              person.isFinance
                ? 'Sees what each hour is worth and marks time as invoiced. Not approving — that says the hours are real, which is somebody else\'s call.'
                : 'Adds the invoicing to whatever they already are. They see amounts on the time log and can record that an invoice has gone out.'
            }
          >
            <FinanceToggle person={person} onDone={onChanged} onError={onError} />
          </ActionRow>
        )}

        <ActionRow
          label="Password"
          detail="Sends a reset link where mail is configured, and shows a temporary password where it is not."
        >
          <ResetPasswordButton person={person} onDone={onHandover} />
        </ActionRow>

        <ActionRow
          label="Two-step sign-in"
          detail="The recovery path when somebody has lost their authenticator. The secret is destroyed, never shown, and they set it up again."
        >
          <ClearTwoFactorButton person={person} onDone={onChanged} onError={onError} />
        </ActionRow>

        <ActionRow
          label="Account status"
          detail={
            person.isActive
              ? 'Deactivating ends their access and keeps everything they did, with their name on it. This is the right answer for somebody who has left.'
              : 'Deactivated. They cannot sign in, and their work is untouched.'
          }
        >
          <ToggleActiveButton person={person} onDone={onChanged} />
        </ActionRow>

        <ActionRow
          label="Delete"
          detail="For an account created by mistake. Refused if they carry any work or logged time, and it cannot be undone."
        >
          <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>
            Delete…
          </button>
        </ActionRow>
      </div>
    </Modal>
  );
}

/** One labelled action: what it is, what it does, and the control that does it. */
function ActionRow({
  label,
  detail,
  children,
}: {
  label: string;
  detail: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="row-between"
      style={{ gap: 12, padding: '9px 0', borderBottom: '1px solid var(--line)', alignItems: 'flex-start' }}
    >
      <div className="grow" style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
          {detail}
        </div>
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}
